<template>
  <div v-if="series.length > 0" class="base-chart-container">
    <apex-chart
      ref="chart"
      :type="type"
      :series="series"
      :options="fullOptions"
      :height="resolvedHeight"
      :width="width"
    />
  </div>
</template>

<script>
/**
 * A component for displaying various types of charts
 * Using apexcharts https://apexcharts.com
 */
import ApexChart from 'vue-apexcharts';
import {
  getHorizontalBarAxisHeadroomMax,
  hasOnlyNonNegativeBarData,
} from '../mixins/chartTableHeightSync.mixin';

export default {
  name: 'BaseChart',
  components: { ApexChart },
  props: {
    type: {
      type: String,
      default: 'pie',
    },
    series: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Object,
      default() {
        return {};
      },
    },
    height: {
      type: String,
      default: '450px',
    },
    width: {
      type: String,
      default: '100%',
    },
    enrichClick: {
      type: Object,
      default() {
        return {};
      },
    },
    downloadFileName: {
      type: String,
      default: 'Chart',
    },
    /**
     * When > 0 the chart paginates internally, showing this many data points
     * per page with prev/next controls. 0 (default) disables pagination.
     */
    pageSize: {
      type: Number,
      default: 0,
    },
    /**
     * Keep the legacy bar auto-height behavior by default.
     * Set false when a parent needs to control bar-chart height explicitly.
     */
    autoBarHeight: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isLabelsWrapped: false,
      seriesVisibility: [],
      currentPage: 1,
      // Tracks the y-axis label offsetY for horizontal bar charts.
      // Updated after word-wrap so multi-line labels stay centered on bars.
      horizontalBarLabelOffsetY: 4,
    };
  },
  computed: {
    isHorizontalBar() {
      return (
        this.type === 'bar' &&
        !!(
          this.options.plotOptions &&
          this.options.plotOptions.bar &&
          this.options.plotOptions.bar.horizontal
        )
      );
    },

    /** True when pagination is active and there is more than one page. */
    isPaginated() {
      return this.pageSize > 0 && this.dataLength > this.pageSize;
    },

    /** Total data points in the first series (used for page maths). */
    dataLength() {
      return this.series[0] && Array.isArray(this.series[0].data)
        ? this.series[0].data.length
        : 0;
    },

    totalPages() {
      return Math.ceil(this.dataLength / this.pageSize) || 1;
    },

    /** Slice of series data for the current page. */
    activeSeries() {
      if (!this.isPaginated) return this.series;
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.series.map((s) =>
        Array.isArray(s.data)
          ? Object.assign({}, s, { data: s.data.slice(start, end) })
          : s
      );
    },

    /** fullOptions scoped to the current page's labels/categories. */
    activeOptions() {
      if (!this.isPaginated) return this.fullOptions;
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      const opts = Object.assign({}, this.fullOptions);
      if (Array.isArray(opts.labels)) {
        opts.labels = opts.labels.slice(start, end);
      }
      if (opts.xaxis && Array.isArray(opts.xaxis.categories)) {
        opts.xaxis = Object.assign({}, opts.xaxis, {
          categories: opts.xaxis.categories.slice(start, end),
        });
      }
      return opts;
    },
    chartEvents() {
      const pointSelectionCharts = ['donut', 'pie', 'polarArea', 'bar'];
      if (pointSelectionCharts.includes(this.type)) {
        return {
          dataPointSelection: this.handleDataPointSelection,
          legendClick: this.handleLegendClick,
        };
      } else {
        return {
          click: this.handleClick,
          legendClick: this.handleLegendClick,
        };
      }
    },
    fullOptions() {
      const baseOptions = {
        legend: {
          position: 'bottom',
        },
        plotOptions: {
          bar: {
            dataLabels: {
              position: 'top',
            },
          },
        },
        chart: {
          background: 'transparent',
          toolbar: {
            show: false,
            tools: {
              download: false,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              reset: false,
              pan: false,
            },
            export: {
              csv: {
                filename: this.downloadFileName,
              },
              svg: {
                filename: this.downloadFileName,
              },
              png: {
                filename: this.downloadFileName,
              },
            },
          },
          events: this.chartEvents,
        },
      };
      if (this.type === 'bar') {
        baseOptions.dataLabels = {
          enabled: false,
          style: {
            colors: ['#333'],
          },
          offsetY: -20,
          orientation: 'vertical',
        };
        (baseOptions.xaxis = {
          labels: {
            rotate: -45,
            rotateAlways: true,
            trim: true,
            show: false,
          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
        }),
          // Disable hiding dataSeries when legend clicked
          (baseOptions.legend = {
            onItemClick: {
              toggleDataSeries: false,
            },
            onItemHover: {
              highlightDataSeries: true,
            },
          });
        baseOptions.tooltip = {
          followCursor: true,
          shared: true,
          intersect: false,
        };
      }
      if (this.options.chart !== undefined) {
        if (
          this.options.chart.toolbar &&
          typeof this.options.chart.toolbar == 'object'
        ) {
          baseOptions.chart.toolbar = this.options.chart.toolbar;
        }
      }
      this.options.chart
        ? Object.assign(this.options.chart, baseOptions.chart)
        : '';
      return this.applyBarRefinements(
        Object.assign({}, baseOptions, this.options)
      );
    },
    resolvedHeight() {
      if (this.type !== 'bar') return this.height;
      if (!this.autoBarHeight) return this.height;
      return this.barChartHeight();
    },
  },
  watch: {
    options: {
      handler: function () {
        // running this handler for area charts messes us up render
        if (this.type === 'area') return;
        if (this && this.$refs && this.$refs.chart) {
          this.$refs.chart.refresh();
        }
      },
      deep: true,
    },
    'options.xaxis': {
      deep: true,
      handler() {
        if (this.type == 'bar' && !this.isLabelsWrapped) {
          this.options.xaxis.categories = this.options.xaxis.categories.map(
            (label) => this.wordWrapToStringList(label, 25)
          );
          this.isLabelsWrapped = true;
          // For horizontal bars, compensate for ApexCharts' multiY over-correction.
          // Single-line labels need offsetY≈4; 2-line wrapped labels need ≈9.
          if (this.isHorizontalBar) {
            const maxLines = this.options.xaxis.categories.reduce(
              (max, label) =>
                Math.max(max, Array.isArray(label) ? label.length : 1),
              1
            );
            this.horizontalBarLabelOffsetY = maxLines >= 2 ? 9 : 4;
          }
          if (this && this.$refs && this.$refs.chart) {
            this.$refs.chart.refresh();
          }
        }
      },
    },
  },
  created() {
    if (this.type == 'line') {
      for (
        var seriesIndex = 0;
        seriesIndex < this.series.length;
        seriesIndex++
      ) {
        this.seriesVisibility[seriesIndex] = 1;
      }
    }
  },
  methods: {
    /**
     * Applies bar-chart visual defaults after the consumer's options have been
     * merged in. Consumer values always win — defaults only fill gaps.
     * Extracted to keep fullOptions below the cyclomatic-complexity limit.
     */
    // eslint-disable-next-line complexity
    normalizeYAxis(yaxis, isHorizontal = false) {
      const defaults = {
        axisBorder: { show: false },
        axisTicks: { show: false },
      };
      const labelDefaults = isHorizontal
        ? { offsetY: this.horizontalBarLabelOffsetY }
        : {};
      if (Array.isArray(yaxis)) {
        return yaxis.map((y) => {
          const merged = Object.assign({}, defaults, y);
          if (isHorizontal) {
            merged.labels = Object.assign({}, labelDefaults, y && y.labels);
          }
          return merged;
        });
      }
      const merged = Object.assign({}, defaults, yaxis || {});
      if (isHorizontal) {
        merged.labels = Object.assign({}, labelDefaults, yaxis && yaxis.labels);
      }
      return merged;
    },
    getHorizontalBarDataLabelOffsetX(series) {
      const safeSeries = Array.isArray(series) ? series : [];
      let maxLabelLength = 0;

      for (let i = 0; i < safeSeries.length; i += 1) {
        const values = Array.isArray(safeSeries[i] && safeSeries[i].data)
          ? safeSeries[i].data
          : [];
        for (let j = 0; j < values.length; j += 1) {
          const point = values[j];
          const normalized =
            point &&
            typeof point === 'object' &&
            Object.prototype.hasOwnProperty.call(point, 'y')
              ? point.y
              : point;
          const numeric = Number(normalized);
          const text = Number.isFinite(numeric)
            ? `${Math.round(Math.abs(numeric))}`
            : `${normalized || ''}`;
          maxLabelLength = Math.max(maxLabelLength, text.length);
        }
      }

      // Apex bar labels may render centered on the anchor point even when
      // textAnchor is set. Use a width-based default so large values are placed
      // fully outside the bar end.
      return Math.max(15, Math.ceil(maxLabelLength * 4.2));
    },
    barChartHeight() {
      const count = this.isPaginated
        ? this.activeSeries[0].data.length
        : this.dataLength;
      const legendShown = !(
        this.options.legend && this.options.legend.show === false
      );
      return `${Math.max(185, count * 55 - (legendShown ? 55 : 0))}px`;
    },
    // eslint-disable-next-line complexity
    applyBarRefinements(merged) {
      if (this.type !== 'bar') return merged;
      if (!merged.plotOptions || !merged.plotOptions.bar) return merged;

      // Rounded corners and compact bar height
      merged.plotOptions = Object.assign({}, merged.plotOptions, {
        bar: Object.assign(
          { borderRadius: 6, borderRadiusApplication: 'end', barHeight: '50%' },
          merged.plotOptions.bar
        ),
      });

      const isHorizontal = !!merged.plotOptions.bar.horizontal;

      // Axis cleanup: hide tick marks and border lines by default
      merged.xaxis = Object.assign(
        {
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { show: false },
        },
        merged.xaxis
      );
      merged.yaxis = this.normalizeYAxis(merged.yaxis, isHorizontal);

      // Horizontal bars: the numeric count axis labels add no useful info
      const isStacked = !!(merged.chart && merged.chart.stacked);
      const stackType = merged.chart && merged.chart.stackType;
      const isPercentStack = isStacked && stackType === '100%';
      let hasVisibleHorizontalEndLabels = false;
      let horizontalEndLabelOffsetX = 15;
      if (isHorizontal) {
        merged.xaxis = Object.assign({ labels: { show: false } }, merged.xaxis);

        // Anchor the data label at the right end of each bar (position:'top' means
        // the value end for horizontal charts), then start the text from that point
        // so it sits fully to the right of the bar.
        merged.plotOptions.bar = Object.assign(
          { dataLabels: { position: 'top' } },
          merged.plotOptions.bar
        );

        // Show the bar value to the right of each bar — but not for stacked
        // charts, where per-segment labels overlap and the total is ambiguous.
        const consumerDataLabels = this.options.dataLabels || {};
        const defaultHorizontalOffsetX = this.getHorizontalBarDataLabelOffsetX(
          this.series
        );
        merged.dataLabels = Object.assign(
          {
            enabled: !isStacked,
            textAnchor: 'start',
            offsetX: defaultHorizontalOffsetX,
            style: { fontSize: '12px', colors: ['#475569'] },
          },
          consumerDataLabels
        );
        const consumerHasExplicitXMax = !!(
          this.options &&
          this.options.xaxis &&
          Object.prototype.hasOwnProperty.call(this.options.xaxis, 'max')
        );
        const consumerHasExplicitXMin = !!(
          this.options &&
          this.options.xaxis &&
          Object.prototype.hasOwnProperty.call(this.options.xaxis, 'min')
        );
        const hasRegularEndLabels = !!(
          merged.dataLabels && merged.dataLabels.enabled
        );
        const hasStackedTotalEndLabels = !!(
          merged.plotOptions &&
          merged.plotOptions.bar &&
          merged.plotOptions.bar.dataLabels &&
          merged.plotOptions.bar.dataLabels.total &&
          merged.plotOptions.bar.dataLabels.total.enabled
        );
        hasVisibleHorizontalEndLabels =
          hasRegularEndLabels || hasStackedTotalEndLabels;

        if (hasStackedTotalEndLabels) {
          const totalOffsetX = Number(
            merged.plotOptions.bar.dataLabels.total.offsetX
          );
          horizontalEndLabelOffsetX = Number.isFinite(totalOffsetX)
            ? Math.abs(totalOffsetX)
            : 6;
        } else {
          const resolvedOffsetX = Number(merged.dataLabels.offsetX);
          horizontalEndLabelOffsetX = Number.isFinite(resolvedOffsetX)
            ? Math.abs(resolvedOffsetX)
            : defaultHorizontalOffsetX;
        }

        if (!consumerHasExplicitXMax && hasVisibleHorizontalEndLabels) {
          const horizontalHeadroomRatio = isPercentStack
            ? 1.08
            : isStacked
            ? 1.02
            : 1.15;
          const xAxisMax = getHorizontalBarAxisHeadroomMax(this.series, {
            isStacked,
            stackType,
            headroomRatio: horizontalHeadroomRatio,
          });
          if (xAxisMax !== undefined) {
            const xAxis = Object.assign({}, merged.xaxis, { max: xAxisMax });
            if (
              !consumerHasExplicitXMin &&
              hasOnlyNonNegativeBarData(this.series)
            ) {
              // Keep positive-only horizontal bars anchored at zero.
              // Without this, Apex may auto-pick a negative minimum when max is
              // explicitly set for data-label headroom, creating a left-side gap.
              xAxis.min = 0;
            }
            merged.xaxis = xAxis;
          }
        }
      }

      // Grid: theme-aware row separators; suppress cross-bar grid lines.
      // For horizontal bars: yaxis lines = horizontal row separators (show),
      //                      xaxis lines = vertical lines across bars (hide).
      // Same logic applies to vertical bars (yaxis = value lines, xaxis = column dividers).
      merged.grid = Object.assign(
        {
          show: true,
          borderColor: 'var(--sb-table-row-border, #f1f5f9)',
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
          ...(isHorizontal && hasVisibleHorizontalEndLabels
            ? {
                padding: {
                  right: Math.max(
                    48,
                    Math.ceil(horizontalEndLabelOffsetX + 28)
                  ),
                },
              }
            : {}),
        },
        merged.grid
      );

      return merged;
    },

    wordWrapToStringList(text, maxLength) {
      let result = [],
        line = [];
      let length = 0;
      let splittedTextArray = text.split(' ');
      splittedTextArray.forEach((word, index) => {
        if (word.length > maxLength) {
          result.push(word.slice(0, maxLength));
          splittedTextArray[index + 1] =
            word.slice(maxLength) + ' ' + splittedTextArray[index + 1];
          length = 0;
          return;
        }
        if (length + word.length >= maxLength) {
          result.push(line.join(' '));
          line = [];
          length = 0;
        }
        length += word.length + 1;
        line.push(word);
      });
      if (line.length > 0) {
        result.push(line.join(' '));
      }
      return result;
    },
    refreshChart() {
      this.$refs.chart.refresh();
    },
    handleLegendClick(chartContext, seriesIndex) {
      const label = this.series[seriesIndex].name;
      const eventData = {
        value: label,
        label,
        series: label,
        seriesIndex,
        enrichment: this.enrichClick,
      };
      if (this.seriesVisibility[seriesIndex] == 0) {
        this.seriesVisibility[seriesIndex] = 1;
      } else {
        this.seriesVisibility[seriesIndex] = 0;
      }
      if (this.$listeners['legend-clicked'] !== undefined) {
        this.$emit('legend-clicked', eventData);
      } else {
        this.$emit('clicked', eventData);
      }
    },
    handleDataPointSelection(event, chartContext, config) {
      const { dataPointIndex, seriesIndex } = config;
      this.$emit('clicked', {
        value: this.series[dataPointIndex],
        label: this.options.labels[dataPointIndex],
        series:
          Object.keys(this.series[seriesIndex]).length > 0
            ? this.series[seriesIndex].name
            : 'default', // only one series in these
        seriesIndex,
        dataPointIndex,
        enrichment: this.enrichClick,
      });
    },
    getSeriesName(seriesIndex) {
      //with 3 lines in chart, apexchart may return invalid series index
      // Use one line only to get exact series name as a workaround for now.
      let lineCount = 0;

      for (let i = 0; i < this.seriesVisibility.length; i++) {
        if (this.seriesVisibility[i] === 1) {
          lineCount++;
        }
      }
      if (lineCount === 1) {
        for (let i = 0; i < this.seriesVisibility.length; i++) {
          if (this.seriesVisibility[i] === 1) {
            return this.series[i].name;
          }
        }
      }

      return this.series[seriesIndex].name;
    },
    handleClick(event, chartContext, config) {
      const { seriesIndex, dataPointIndex } = config;
      let value = undefined;
      let series = undefined;
      if (typeof this.series[seriesIndex] === 'object') {
        value = 0;
        if (config.config.series[seriesIndex].data.length) {
          value = this.series[seriesIndex].data[dataPointIndex];
        }
        series = this.getSeriesName(seriesIndex);
      } else {
        value = this.series[dataPointIndex];
        series = 'default';
      }

      const label = this.options.labels
        ? this.options.labels[dataPointIndex]
        : undefined;
      if (value === undefined && label === undefined) {
        console.warn(
          `Unable to get value and label, this may not be an issue is this was a legend click`
        );
        return;
      }

      this.$emit('clicked', {
        value,
        label,
        series,
        seriesIndex,
        dataPointIndex,
        enrichment: this.enrichClick,
      });
    },
  },
};
</script>

<style>
/* ── Bar chart refinements ────────────────────────────────────────────────────
   Y-axis label colour uses --text2 so it reads well in both modes.
   First and last horizontal grid lines are hidden; only the inner separators
   between bars remain, matching the design's row-divider pattern.            */

.base-chart-container .apexcharts-yaxis text,
.base-chart-container .apexcharts-datalabel {
  fill: var(--text2, #475569) !important;
}

/* Top and bottom boundary lines live in .apexcharts-grid-borders, not in
   .apexcharts-gridlines-horizontal (which contains only inner bar dividers).
   Hiding this group removes the outer boundaries while leaving every inner
   separator between bars intact. */
.base-chart-container .apexcharts-grid-borders line {
  stroke-opacity: 0 !important;
}

/* ── Chart container ──────────────────────────────────────────────────────────
   Outer border, equal inner spacing, and surface background.
   --border and --surface auto-remap in dark mode via Variables.css.
   --sb-table-radius (10px) matches the panel/card corner radius token.       */
.base-chart-container {
  border: 1px solid var(--border, #e2e8f0);
  border-radius: var(--sb-table-radius, 10px);
  background: var(--surface, #ffffff);
  padding: 0 12px;
  box-sizing: border-box;
}

g:hover {
  cursor: pointer;
}

/* ── ApexCharts dark mode ────────────────────────────────────────────────────
   ApexCharts injects its own HTML/CSS for tooltips and legend text. Those
   elements live outside the Vue component tree, so only global CSS can reach
   them. All values use Variables.css tokens — no hardcoded colours.
   Covers both explicit dark (.dark on <html>) and auto dark (system pref).   */

/* Tooltip container */
html.dark .apexcharts-tooltip {
  background: var(--surface) !important;
  border: 1px solid var(--border) !important;
  color: var(--text) !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4) !important;
}

/* Tooltip title row */
html.dark .apexcharts-tooltip-title {
  background: var(--surface2) !important;
  border-bottom: 1px solid var(--border) !important;
  color: var(--text) !important;
}

/* Tooltip series rows */
html.dark .apexcharts-tooltip-series-group {
  background: var(--surface) !important;
}

/* Tooltip text */
html.dark .apexcharts-tooltip-text-y-label,
html.dark .apexcharts-tooltip-text-y-value,
html.dark .apexcharts-tooltip-text-goals-label,
html.dark .apexcharts-tooltip-text-goals-value,
html.dark .apexcharts-tooltip-text-z-value {
  color: var(--text) !important;
}

/* Legend text */
html.dark .apexcharts-legend-text {
  color: var(--text2) !important;
}

/* Donut / pie center labels (Total label + value) */
html.dark .apexcharts-datalabel-label,
html.dark .apexcharts-datalabel-value {
  fill: var(--text) !important;
}

/* ── Auto dark (system preference, no explicit theme class on <html>) ──────── */
@media (prefers-color-scheme: dark) {
  html:not(.light):not(.dark) .apexcharts-tooltip {
    background: var(--surface) !important;
    border: 1px solid var(--border) !important;
    color: var(--text) !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4) !important;
  }
  html:not(.light):not(.dark) .apexcharts-tooltip-title {
    background: var(--surface2) !important;
    border-bottom: 1px solid var(--border) !important;
    color: var(--text) !important;
  }
  html:not(.light):not(.dark) .apexcharts-tooltip-series-group {
    background: var(--surface) !important;
  }
  html:not(.light):not(.dark) .apexcharts-tooltip-text-y-label,
  html:not(.light):not(.dark) .apexcharts-tooltip-text-y-value,
  html:not(.light):not(.dark) .apexcharts-tooltip-text-goals-label,
  html:not(.light):not(.dark) .apexcharts-tooltip-text-goals-value,
  html:not(.light):not(.dark) .apexcharts-tooltip-text-z-value {
    color: var(--text) !important;
  }
  html:not(.light):not(.dark) .apexcharts-legend-text {
    color: var(--text2) !important;
  }

  /* Donut / pie center labels */
  html:not(.light):not(.dark) .apexcharts-datalabel-label,
  html:not(.light):not(.dark) .apexcharts-datalabel-value {
    fill: var(--text) !important;
  }
}
</style>
